import { useEffect, useRef } from 'react';

const DEFAULT_MESSAGE =
  'You have unsaved changes. Are you sure you want to leave this page?';

const getHistoryIndex = (state: unknown): number | null => {
  if (typeof state !== 'object' || state === null || !('idx' in state)) {
    return null;
  }
  const index = (state as { idx: unknown }).idx;
  return typeof index === 'number' ? index : null;
};

/**
 * Warns before leaving a page with unsaved changes.
 *
 * BrowserRouter does not expose the data-router blocker API, so internal link
 * clicks and history navigation are guarded at the browser boundary.
 */
export function useUnsavedChangesWarning(
  hasUnsavedChanges: boolean,
  message = DEFAULT_MESSAGE,
) {
  const skipNextPopRef = useRef(false);
  const historyIndexRef = useRef<number | null>(
    getHistoryIndex(globalThis.history.state as unknown),
  );

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    const handleDocumentClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      const anchor =
        target instanceof Element ? target.closest<HTMLAnchorElement>('a[href]') : null;
      if (
        !anchor ||
        anchor.target === '_blank' ||
        anchor.hasAttribute('download')
      ) {
        return;
      }

      const destination = new URL(anchor.href, globalThis.location.href);
      if (
        destination.origin !== globalThis.location.origin ||
        destination.href === globalThis.location.href
      ) {
        return;
      }

      if (!globalThis.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      const nextIndex = getHistoryIndex(event.state as unknown);

      if (skipNextPopRef.current) {
        skipNextPopRef.current = false;
        historyIndexRef.current = nextIndex;
        return;
      }

      if (globalThis.confirm(message)) {
        historyIndexRef.current = nextIndex;
        return;
      }

      const currentIndex = historyIndexRef.current;
      const restoreDelta =
        currentIndex !== null && nextIndex !== null
          ? currentIndex - nextIndex
          : 1;
      skipNextPopRef.current = true;
      globalThis.history.go(restoreDelta || 1);
    };

    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    globalThis.document.addEventListener('click', handleDocumentClick, true);
    globalThis.addEventListener('popstate', handlePopState);

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.document.removeEventListener('click', handleDocumentClick, true);
      globalThis.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, message]);

  return () =>
    !hasUnsavedChanges ||
    globalThis.confirm(message);
}
